import React from 'react';
import { Row, Col, Input, Button, Space, Typography } from 'antd';
import styles from './styles.module.scss';

const { Text } = Typography;

interface Dimensions {
  height: string;
  width: string;
  length: string;
  weight: string;
  lengthUnit: 'cm' | 'ft/in';
  weightUnit: 'g' | 'kg';
}

interface DimensionInputsProps {
  dimensions: Dimensions;
  onDimensionChange: (field: keyof Dimensions, value: string) => void;
  onUnitChange: (field: 'lengthUnit' | 'weightUnit', value: string) => void;
  title?: string;
  className?: string;
}

const DIMENSION_FIELDS = [
  { key: 'height', label: 'Height', placeholder: '', unitType: 'lengthUnit' },
  { key: 'width', label: 'Width', placeholder: '', unitType: 'lengthUnit' },
  { key: 'length', label: 'Length', placeholder: '', unitType: 'lengthUnit' },
  { key: 'weight', label: 'Weight', placeholder: '', unitType: 'weightUnit' },
] as const;

const UNIT_OPTIONS = {
  lengthUnit: [
    { value: 'cm', label: 'cm' },
    { value: 'ft/in', label: 'ft/in' },
  ],
  weightUnit: [
    { value: 'g', label: 'g' },
    { value: 'kg', label: 'kg' },
  ],
} as const;

export const DimensionInputs: React.FC<DimensionInputsProps> = ({
  dimensions,
  onDimensionChange,
  onUnitChange,
  title,
  className,
}) => {
  return (
    <div className={className}>
      {/* Section Title */}
      <Row align="middle" justify="space-between" className={styles.dimensionsHeader}>
        {title && (
          <Col>
            <Text className={styles.sectionTitle}>{title}</Text>
          </Col>
        )}

        {/* Unit Toggle Buttons */}
        <Col>
          <Space size={8} className={styles.unitToggle}>
            {UNIT_OPTIONS.lengthUnit.map((option) => (
              <Button
                key={option.value}
                type={dimensions.lengthUnit === option.value ? 'primary' : 'default'}
                size="small"
                onClick={() => onUnitChange('lengthUnit', option.value)}
                className={styles.unitButton}
              >
                {option.label}
              </Button>
            ))}
            {UNIT_OPTIONS.weightUnit.map((option) => (
              <Button
                key={option.value}
                type={dimensions.weightUnit === option.value ? 'primary' : 'default'}
                size="small"
                onClick={() => onUnitChange('weightUnit', option.value)}
                className={styles.unitButton}
              >
                {option.label}
              </Button>
            ))}
          </Space>
        </Col>
      </Row>

      {/* Dimension Input Fields */}
      <Row gutter={[16, 16]} className={styles.dimensionsGrid}>
        {DIMENSION_FIELDS.map((field) => {
          const unitValue = field.unitType === 'lengthUnit' ? dimensions.lengthUnit : dimensions.weightUnit;

          return (
            <Col xs={12} key={field.key}>
              <Text className={styles.fieldLabel}>{field.label}</Text>
              <Input
                type="number"
                placeholder={field.placeholder}
                value={dimensions[field.key]}
                onChange={(e) => onDimensionChange(field.key, e.target.value)}
                suffix={<Text className={styles.inputSuffix}>{unitValue}</Text>}
                className={styles.dimensionInput}
              />
            </Col>
          );
        })}
      </Row>
    </div>
  );
};
